import React, { useState, useEffect, useRef } from "react";
import Heart from "../../assets/icons/heart.svg";
import Visit from "../../assets/icons/visit-product-icon.svg";
import ControlPane from "../ControlPane";
import useRoomAnalysis from "../../services/hooks/useRoomAnalysis";
import useProductStaging from "../../services/hooks/useProductStaging";
import SwipeableEdgeDrawer from "../SwipeableEdgeDrawe";
import useSaveEmail from "../../services/hooks/useSaveEmail";
import useLogRating from "../../services/hooks/useLogRating";
import useLogCheckout from "../../services/hooks/useLogCheckout";
import useInteractiveSofa from "../../services/hooks/useInteractiveSofa";
import useInteractiveSofaStaging from "../../services/hooks/useInteractiveSofaStaging"
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../Loader";
import { Rocket } from 'lucide-react';
import { ShoppingCart, RotateCcw } from 'lucide-react';

import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import '@mui/material/styles';

import Popup from "../Popup";



const { ControlPaneComponent, EngagementBar, buttonBaseClasses, EmailForm } = ControlPane;

const isUsingTabs = false; 

const emptyUserGeometryInputs = {
  x: null,
  y: null, 
  rotationY: null,
  position_source: null,
  areValuesSelectedByUser: false, 
}

const RoomAnalysisStagingStep = ({
  showModal,
  setShowModal,
  productInfo,
  setAsSelectedProduct,
  clickedImageKey,
  image,
  setRoomAnalysisLoading,
  images,
  vendorColor,
  setproductStagingLoading,
  setImage,
  setInteractiveSofaLoading,
  screenWidthConsts,
}) => {
  const {selected_product, product_list, product_color_options, product_size_options, tabs } = productInfo;
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [activeItem, setActiveItem] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(selected_product);
  const [hideStagedSofa, setHideStagedSofa] = useState(false); // For "View Original" button
  const [showBoundingBox, setShowBoundingBox] = useState(true);

  const {analysisResult, error, setError, analyzeRoom, loading } = useRoomAnalysis();

  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [mobileScreen, setMobileScreen] = useState(false);

  // Function to check screen size and update state
  const checkScreenSize = () => {
    setIsSmallScreen(window.innerWidth < screenWidthConsts.small);
    setMobileScreen(window.innerWidth < screenWidthConsts.mobile);
  };


  // Add event listener on component mount and remove on unmount
  useEffect(() => {
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const handleAnalyzeRoom = (imgFile) => {
    if (imgFile) {
      analyzeRoom({ templateImage: null, roomImageFile: imgFile });
    } else {
      analyzeRoom({ templateImage: clickedImageKey, roomImageFile: null });
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setActiveItem(null);
  };
  const handleItemClick = (item) => {
    setActiveItem(item);
  };
  useEffect(() => {
    if (selectedProduct) {
      if (image) {
        handleAnalyzeRoom(image);
      } else {
        handleAnalyzeRoom(null);
      }
    }
  }, [image]);

  // Handle room analysis errors
  useEffect(() => {
    if (error) {
      setTimeout(() => {
        GoToRoomSelection()
        setError(null);
      }, 6000);
    }
  }, [error]);

  // Staging Product ------------------------------------------------------------------------------------------------
  const { stagingResult, setStagingResult, error: stagingError, stageProduct, loading: productStagingLoading } = useProductStaging();
  const handleStageProduct = (product_id) => {
    stageProduct(analysisResult.image_hash, product_id);
  };

  useEffect(() => {
    setRoomAnalysisLoading(loading);
  }, [loading]);

  useEffect(() => {
    setproductStagingLoading(productStagingLoading);
    if (!productStagingLoading) {
       setHideStagedSofa(false);
    }
  }, [productStagingLoading]);

  // Email & Log Checkout -----------------------------------------------------------------------------------------------------------
  const { status, error: saveEmailError, saveEmail, setStatus } = useSaveEmail();
  const [email, setEmail] = useState("");
  // Use Log checkout
  const { response, logCheckout } = useLogCheckout();
  const handleCheckout = () => {
    handleCheckoutFromProduct(selectedProduct.product_id, selectedProduct.product_url)
  };

  const handleCheckoutFromProduct = (product_id, product_url) => {
    logCheckout(product_id);
    window.location.href = product_url;
  };

  const handleSaveEmail = (e) => {
    e.preventDefault();
    saveEmail(email);
  };

  useEffect(() => {
    let timer;
    if (status === "success") {
      setEmail("");
      toast.success("Thank you for providing your email!");
      timer = setTimeout(() => {
        setStatus("idle");
      }, 2000);
    }

    return () => clearTimeout(timer);
  }, [status]);

  // Price -----------------------------------------------------------------------------------------------------------

  function getPrice(product) {
      // Convert min_price and max_price strings to numbers
      const minPrice = parseFloat(product.min_price);
      const maxPrice = parseFloat(product.max_price);
      const locale = undefined; // Use the user's locale

      // Set up number formatter without decimals
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: product.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });

      // Format the price based on whether min and max are the same
      let res;
      if (minPrice === maxPrice) {
        res = formatter.format(minPrice);
      } else {
        res = `${formatter.format(minPrice)} - ${formatter.format(maxPrice)}`;
      }

      return res;
  }

  // Search & Filter --------------------------------------------------------------------------------------------------
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState(product_list);

  const [showFilters, setShowFilters] = useState(false);

  // Define state to track selected checkboxes
  const [selectedProductSizes, setSelectedProductSizes] = useState([]);
  const [selectedProductColors, setSelectedProductColors] = useState([]);

  const productColorHtmlMap = {
      Brown: "#836953",
      Green: "#8fbc8f",
      Gray: "#a9a9a9",
      White: "#FFFFFF",

      // Add more colors as needed
  };
    
  // Handle size checkbox change
  const handleProductSizeChange = (size) => {
    setSelectedProductSizes((prev) =>
      prev.includes(size)
        ? prev.filter((item) => item !== size)  // Deselect size if already selected
        : [...prev, size]  // Select size if not already selected
    );
  };

  // Handle color checkbox change
  const handleProductColorChange = (color) => {
    setSelectedProductColors((prev) =>
      prev.includes(color)
        ? prev.filter((item) => item !== color)  // Deselect color if already selected
        : [...prev, color]  // Select color if not already selected
    );
  };

  // Function to count the total number of selected filters (sizes + colors)
  const getAppliedFiltersCount = () => {
    return selectedProductSizes.length + selectedProductColors.length;
  };

  const handleFilterClick = () => {
    setShowFilters(!showFilters);
  };

  const handleClearFilters = () => {
    // Reset any filter states and close filter view
    setSelectedProductColors((prev) => []);
    setSelectedProductSizes((prev) => []);
  };

  const handleDone = () => {
    setShowFilters(false);
  };

  useEffect(() => {
      // Only apply filtering when showFilters is false
      if (!showFilters) {
        setFilteredProducts(
          product_list.filter((product) => {
            // Filter by title if searchTerm exists
            const matchesSearch = searchTerm === "" || product.title.toLowerCase().includes(searchTerm.toLowerCase());

            // Filter by selected sizes
            const matchesSize =
              selectedProductSizes.length === 0 || selectedProductSizes.includes(product.product_size);

            // Filter by selected colors
            const matchesColor =
              selectedProductColors.length === 0 || selectedProductColors.includes(product.product_color);

            const matchesTab = product.tabs.includes(activeTab);

            // Combine all filters: title, size, and color
            return matchesSearch && matchesSize && matchesColor && matchesTab;
          })
        );
      }
  }, [searchTerm, showFilters, product_list, selectedProductSizes, selectedProductColors, activeTab]);

 // ------------ Rating ----------------------------------------------------------------------------------------------
  
  const { logRating } = useLogRating();

  const [showRatingDropdown, setShowRatingDropdown] = useState(false);
  const [rating, setRating] = useState(0);
  const [showRatingToast, setShowRatingToast] = useState(false);

  const handleRating = (newRating) => {
    setRating(newRating);
    logRating(newRating)
    setShowRatingDropdown(false);
    setShowRatingToast(true);
  };

  const handleRatingDropdownClick = () => {
    setShowRatingDropdown(!showRatingDropdown);
  };


  useEffect(() => {
    let timer;
    if (showRatingToast) {
      setEmail("");
      toast.success("Thanks for rating!");
      timer = setTimeout(() => {
        setStatus("idle");
      }, 2000);
      setShowRatingToast(false); 
    }

    return () => clearTimeout(timer);
  }, [showRatingToast]);


  const ratingArgs = { rating, handleRatingDropdownClick, handleRating, showRatingDropdown, showRatingToast };

  // ------------ Go to room selection ----------------------------------------------------------------------------------
 
  const GoToRoomSelection = () => {
    setShowModal({ ...showModal, level: 1 });
    setImage(null);
  }

 // ------------ product list item -----------------------------------------------------------------------------------

  const [closeSwipeableDrawerOnce, setCloseSwipeableDrawerOnce ] = useState(false); 
  const handleProductListItemClick = (product_id) => {
  const selectedProductNew = product_list.find((product) => product.product_id === product_id);
  setSelectedProduct(selectedProductNew); // Update the selected product locally
  setAsSelectedProduct(selectedProductNew);  // Update the selected product in hooks/useProductInfo.js
  setCloseSwipeableDrawerOnce(true); 
  if (!analysisResult.is_empty_room) {
    if (userGeometryInputs.areValuesSelectedByUser) {
      setIsInteractingNow(true)
    }
    else {
      handleStageProduct(product_id);
    }
  };
};

// ------------- interactive sofa staging ----------------------------------------------------------------------------
 const { stageInteractiveSofa } = useInteractiveSofaStaging()

  const [isInteractingNow, setIsInteractingNow] = useState(analysisResult?.is_empty_room ?? false);

  const [userGeometryInputs, setUserGeometryInputs] = useState(emptyUserGeometryInputs);

  // Confirmed geometry inputs: chosen by the user, clicked 'Generate', and haven't started to rotate again
  const [confirmedUserGeometryInputs, setConfirmedUserGeometryInputs] = useState(emptyUserGeometryInputs);  

  useEffect(() => {
    if (analysisResult === null) return;

    setUserGeometryInputs({
      x: analysisResult.initial_interactive_sofa_x,
      y: analysisResult.initial_interactive_sofa_y,
      rotationY: analysisResult.initial_yaw, // only change when sending post request
      position_source: "RoomAnalysis",
      areValuesSelectedByUser: false, // not initial and not cleared
    });
    if (analysisResult?.is_empty_room) {
      setIsInteractingNow(true); 
    }

  }, [analysisResult]);

  useEffect(() => {
    if (hideStagedSofa) {
      setIsInteractingNow(false); 
    };
  }, [hideStagedSofa]);

  const InteractiveSofaArgs = {
    isInteractingNow: isInteractingNow, 
    setIsInteractingNow: setIsInteractingNow,
    userGeometryInputs: userGeometryInputs,
    setUserGeometryInputs: setUserGeometryInputs,
    confirmedUserGeometryInputs: confirmedUserGeometryInputs, 
    setConfirmedUserGeometryInputs: setConfirmedUserGeometryInputs, 
  }

 // -------------------------------------------------------------------------------------------------------------------
  return (
    <div className="relative pt-4">
      {/* header in mobile devices only */}
      <div className="m-4">
        {mobileScreen 
          ? <MobileHeader
              vendorColor={vendorColor}
              handleCheckout={handleCheckout}
              handleSaveEmail={handleSaveEmail}
              email={email}
              setEmail={setEmail}
              GoToRoomSelection={GoToRoomSelection}
              hideStagedSofa={hideStagedSofa}
              setHideStagedSofa={setHideStagedSofa}
              setShowBoundingBox={setShowBoundingBox}
              hasStagedImage={stagingResult?.room_image_staged}
              ratingArgs={ratingArgs}
              analysisResult={analysisResult}/> 
          : null}
      </div>
      
      <div className="m-4 flex md:gap-4 items-start">
        {/* Drawer on smaller devices to show products */}
        <ToastContainer />
        {MainPane(
              [ activeTab,
                filteredProducts,
                getAppliedFiltersCount,
                getPrice,
                handleCheckoutFromProduct,
                handleClearFilters,
                handleDone,
                handleFilterClick,
                handleProductColorChange,
                handleProductSizeChange,
                handleProductListItemClick,
                handleTabClick,
                productColorHtmlMap,
                productStagingLoading,
                product_color_options,
                product_size_options,
                searchTerm,
                selectedProductColors,
                selectedProductSizes,
                selected_product,
                setSearchTerm,
                showFilters,
                stagingResult,
                tabs,
                vendorColor, 
                closeSwipeableDrawerOnce,
                setCloseSwipeableDrawerOnce, ], 
              isSmallScreen,
          )}
        
        {MainImageContainer(
          stagingResult, 
          setStagingResult,
          analysisResult, 
          vendorColor, 
          hideStagedSofa, 
          showBoundingBox, 
          mobileScreen,
          selectedProduct,
          setInteractiveSofaLoading,
          stageInteractiveSofa,
          setproductStagingLoading,
          InteractiveSofaArgs)}

        {mobileScreen ? null : (
          <div className="mail w-[20%] lg:w-[20%]">
            <ControlPaneComponent
              handleCheckout={handleCheckout}
              handleSaveEmail={handleSaveEmail}
              email={email}
              setEmail={setEmail}
              vendorColor={vendorColor}
              GoToRoomSelection={GoToRoomSelection}
              hideStagedSofa={hideStagedSofa} 
              setHideStagedSofa={setHideStagedSofa} 
              setShowBoundingBox={setShowBoundingBox}
              hasStagedImage={stagingResult?.room_image_staged}
              ratingArgs={ratingArgs}
              usingInteractiveSofaComponent={analysisResult?.is_empty_room ?? false}
            />
          </div>
        )}
      </div>

    </div>
  );
};


function MainPane(mainPaneArgs, isSmallScreen) {
  if (isSmallScreen) {
    return MainPaneMobile(...mainPaneArgs)
  }
  else {
    return MainPaneDesktop(...mainPaneArgs)
  }
}

function MainPaneDesktop(
  activeTab,
  filteredProducts,
  getAppliedFiltersCount,
  getPrice,
  handleCheckoutFromProduct,
  handleClearFilters,
  handleDone,
  handleFilterClick,
  handleProductColorChange,
  handleProductSizeChange,
  handleProductListItemClick,
  handleTabClick,
  productColorHtmlMap,
  productStagingLoading,
  product_color_options,
  product_size_options,
  searchTerm,
  selectedProductColors,
  selectedProductSizes,
  selected_product,
  setSearchTerm,
  showFilters,
  stagingResult,
  tabs,
  vendorColor,
) {
  return <div className="w-[20%] ">
    {isUsingTabs && TabContainer(tabs, activeTab, vendorColor, handleTabClick)}

    {/* Search input + Filter button */}
    <div className="product-list bg-[#F0EDE9] rounded-xl p-4 max-h-[calc(100vh-12rem)] overflow-y-auto">
      <div className="search pt-2 flex items-center justify-between gap-x-4">
        <input
          type="search"
          name="search"
          id="search"
          placeholder="Search..."
          className="w-1/2 py-2 px-4 rounded-xl text-[#5A6066] focus:border-spacing-0 focus:outline-none"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)} />
        {FilterButton(vendorColor, handleFilterClick, getAppliedFiltersCount, showFilters)}
      </div>

      {showFilters ? (
        FilterOptionsComponent(product_size_options, selectedProductSizes, handleProductSizeChange, product_color_options, productColorHtmlMap, selectedProductColors, handleProductColorChange, handleClearFilters, vendorColor, handleDone)
      ) : (
        filteredProducts?.map((product, index) => {
          const isSelected = product.product_id === selected_product.product_id;

          return (
            // ---- One product in product list ---
            <div
              key={index}
              className={`product-card bg-white rounded-xl p-3 my-4 flex gap-4 ${isSelected ? "border-2 border-blue-500" : ""}`}
              onClick={() => {
                handleProductListItemClick(product.product_id);
              } }
            >
              <div className="w-[30%] lg:w-[140px] lg:h-[140px] relative">
                <img
                  src={product.thumbnail_url}
                  alt="product-img"
                  className="rounded-xl w-full h-full object-contain" />
              </div>
              <div className="info w-[60%] flex flex-col justify-between gap-4">
                <div>
                  <p className="text-[#71787F]">{product.manufacturer}</p>
                  <h3 className="text-[13px] text-ellipsis cursor-pointer text-[#2D3033] font-bold overflow-hidden whitespace-nowrap lg:w-[170px]">
                    <span title={product.title}>{product.title}</span>
                  </h3>
                </div>
                <div className="details text-[#71787F]">
                  <p className="text-[14px]">{product.product_size || ""}</p>
                  <p className="text-[14px]">{getPrice(product) || ""}</p>
                  <div className="visit-product flex items-center justify-start gap-1 cursor-pointer">
                    <div
                      onClick={(e) => {
                        e.stopPropagation(); // Stop the click event from bubbling up
                        handleCheckoutFromProduct(product.product_id, product.product_url);
                      } }
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <img src={Visit} alt="visit-product-icon" className="w-3 h-3" />
                      <p className="underline text-[13px]">Visit product page</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  </div>;
}

function MainPaneMobile(
  activeTab,
  filteredProducts,
  getAppliedFiltersCount,
  getPrice,
  handleCheckoutFromProduct,
  handleClearFilters,
  handleDone,
  handleFilterClick,
  handleProductColorChange,
  handleProductSizeChange,
  handleProductListItemClick,
  handleTabClick,
  productColorHtmlMap,
  productStagingLoading,
  product_color_options,
  product_size_options,
  searchTerm,
  selectedProductColors,
  selectedProductSizes,
  selected_product,
  setSearchTerm,
  showFilters,
  stagingResult,
  tabs,
  vendorColor,
  closeSwipeableDrawerOnce,
  setCloseSwipeableDrawerOnce, 
) {
  return <div style={{ opacity: "0.1" }}>
    <SwipeableEdgeDrawer
      vendorColor={vendorColor}
      productStagingLoading={productStagingLoading}
      stagingResult={stagingResult}
      closeSwipeableDrawerOnce={closeSwipeableDrawerOnce}
      setCloseSwipeableDrawerOnce={setCloseSwipeableDrawerOnce}
      productList={<div className="w-[100%] relative gap-4">
        {isUsingTabs && TabContainer(tabs, activeTab, vendorColor, handleTabClick)}
        {/* Search input + Filter button */}
        <div className="product-list bg-[#F0EDE9] rounded-xl p-2 max-h-[calc(-2rem+100vh)] overflow-y-auto">
          <div className="search my-4 pt-2 flex items-center justify-between gap-x-4">
            <input
              type="search"
              name="search"
              id="search"
              placeholder="Search..."
              className="w-1/2 py-2 px-4 mx-auto rounded-xl text-[#5A6066] focus:border-spacing-0 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} />
            {FilterButton(vendorColor, handleFilterClick, getAppliedFiltersCount, showFilters)}
          </div>
          {showFilters ? (
            FilterOptionsComponent(product_size_options, selectedProductSizes, handleProductSizeChange, product_color_options, productColorHtmlMap, selectedProductColors, handleProductColorChange, handleClearFilters, vendorColor, handleDone)
          ) : (
            filteredProducts?.map((product, index) => {
              const isSelected = product.product_id === selected_product.product_id;

              return (
                // ---- One product in product list ---
                <div
                  key={index}
                  className={`product-card bg-white rounded-xl p-3 my-4 flex gap-4 ${isSelected ? "border-2 border-blue-500" : ""}`}
                  onClick={() => {
                    handleProductListItemClick(product.product_id);
                  } }
                >
                  <div className="w-[30%] lg:w-[140px] lg:h-[140px] relative">
                    <img src={product.thumbnail_url} alt="product-img" className="rounded-xl w-full h-full object-contain" />
                    {/* <div className="favorite absolute top-2 right-2 bg-[#2D3033] bg-opacity-50 rounded p-2">
                                <img src={Heart} alt="favorite-icon" />
                                </div> */}
                  </div>
                  <div className="info w-[60%] flex flex-col justify-between gap-4">
                    <div>
                      <p className="text-[#71787F]">{product.manufacturer}</p>
                      <h3 className="text-[13px] text-ellipsis cursor-pointer text-[#2D3033] font-bold overflow-hidden whitespace-nowrap lg:w-[170px]">
                        <span title={product.title}>{product.title}</span>
                      </h3>
                    </div>
                    <div className="details text-[#71787F]">
                      <p className="text-[14px]">{product.product_size || ""}</p>
                      <p className="text-[14px]">{getPrice(product) || ""}</p>

                      <div className="visit-product flex items-center justify-start gap-1 cursor-pointer">
                        <div
                          onClick={(e) => {
                            e.stopPropagation(); // Stop the click event from bubbling up
                            handleCheckoutFromProduct(product.product_id, product.product_url);
                          } }
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <img src={Visit} alt="visit-product-icon" className="w-3 h-3" />
                          <p className="underline text-[13px]">Visit product page</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }))}
        </div>
      </div>} />
  </div>;
}


const InteractiveSofaComponent = ({ 
  imageSrc, imageRef, vendorColor, imageHash, selectedProduct, analysisResult, setInteractiveSofaLoading, mobileScreen, isEmptyRoom,
  isStaged, stageInteractiveSofa, stagingResult, setStagingResult, setProductStagingLoading, InteractiveSofaArgs
  }) => {
  const containerRef = useRef(null);
  const [rotationValueInSlider, setRotationValueInSlider] = useState(-analysisResult.initial_yaw);
  const [sofaData, setSofaData] = useState({
    imageSrc: null,
    position: null,
  });

  const {isInteractingNow, setIsInteractingNow, userGeometryInputs, setUserGeometryInputs, confirmedUserGeometryInputs, setConfirmedUserGeometryInputs} = InteractiveSofaArgs


    // ------------- POST request --------------------------------------------------------------------------------------
 
    // send post request
    useEffect(() => {
      if (!isInteractingNow) return;
      if (!userGeometryInputs.areValuesSelectedByUser && !isEmptyRoom) return;

      setInteractiveSofaLoading(true);

      const containerBounds = containerRef.current.getBoundingClientRect();
      let containerWidth = containerBounds.right - containerBounds.left; 
      let containerHeight = containerBounds.bottom - containerBounds.top; 

      console.log('isEmptyRoom', isEmptyRoom)
      
      getInteractiveSofa(
          userGeometryInputs.x, userGeometryInputs.y, userGeometryInputs.rotationY, containerWidth, containerHeight, imageHash, 
          selectedProduct.product_id, userGeometryInputs.position_source, isEmptyRoom);
      // };
    }, [userGeometryInputs, selectedProduct, isInteractingNow]);

    const { statusInteractiveSofa, errorInteractiveSofa, getInteractiveSofa, setStatusInteractiveSofa, responseInteractiveSofa } = useInteractiveSofa();
    // receive post request result
    useEffect(() => {
      if (statusInteractiveSofa === "success") {
        setSofaData({
          imageSrc: `data:image/png;base64,${responseInteractiveSofa.sofa}`,
        });
        setRotationValueInSlider(-responseInteractiveSofa.rotationY)
      }
      setInteractiveSofaLoading(false);
      setStatusInteractiveSofa(null);
    }, [statusInteractiveSofa]);

    // -- mouse handlers ----------------------------------------------------------------------------------------------------------------------

  const onImageClick = (e) => {
    if (!isEmptyRoom) return; 
    setConfirmedUserGeometryInputs(emptyUserGeometryInputs);
    const containerBounds = containerRef.current.getBoundingClientRect();
  
    setUserGeometryInputs({
      x: e.clientX - containerBounds.left,
      y: e.clientY - containerBounds.top, 
      rotationY: userGeometryInputs.rotationY,
      position_source: "userInput",
      areValuesSelectedByUser: true
    });
    setIsInteractingNow(true);
  };

  // Called when dragging is in progress
  const handleRotationSliderChange = (event, newRotation) => {
    event.stopPropagation(); // Prevent the click from affecting the sofa
    setRotationValueInSlider(newRotation, newRotation);
  };

  // Called when dragging stops or slider is clicked
  const handleRotationSliderCommit = (event, newRotation) => {
    setConfirmedUserGeometryInputs(emptyUserGeometryInputs);
    event.stopPropagation(); // Prevent the click from affecting the sofa

    setConfirmedUserGeometryInputs(emptyUserGeometryInputs);
    
    setUserGeometryInputs({
      x: userGeometryInputs.x,
      y: userGeometryInputs.y, 
      rotationY: -newRotation,
      position_source: userGeometryInputs.position_source,
      areValuesSelectedByUser: true, 
    });
    setIsInteractingNow(true);
  };

  const onIgnoreClick = (e) => {
    e.stopPropagation(); // Prevent the click from affecting the sofa
  };

  const onGenerateClick = (e) => {
      const containerBounds = containerRef.current.getBoundingClientRect();
      let containerWidth = containerBounds.right - containerBounds.left; 
      let containerHeight = containerBounds.bottom - containerBounds.top; 

      const newVals = {
        x: userGeometryInputs.x,
        y: userGeometryInputs.y, 
        rotationY: userGeometryInputs.rotationY,
        position_source: userGeometryInputs.position_source,
        areValuesSelectedByUser: true, 
      }

      setUserGeometryInputs(newVals);
      setConfirmedUserGeometryInputs(newVals)
      
      stageInteractiveSofa(
          userGeometryInputs.x, userGeometryInputs.y, userGeometryInputs.rotationY, containerWidth, containerHeight, imageHash, 
          selectedProduct.product_id, userGeometryInputs.position_source, 
          stagingResult, setStagingResult, setProductStagingLoading, setIsInteractingNow);
  }
  // -------------------------------------------------------------------------------------------------

  useEffect(() => {
    const containerBounds = containerRef.current.getBoundingClientRect();
    let containerWidth = containerBounds.right - containerBounds.left; 
    let containerHeight = containerBounds.bottom - containerBounds.top; 
    console.log('Changed product')
    if (confirmedUserGeometryInputs.x !== null) {
      console.log('Changed product and confirmed new geometry')
      stageInteractiveSofa(
        userGeometryInputs.x, userGeometryInputs.y, userGeometryInputs.rotationY, containerWidth, containerHeight, imageHash, 
        selectedProduct.product_id, userGeometryInputs.position_source, 
        stagingResult, setStagingResult, setProductStagingLoading, setIsInteractingNow);
    };
  }, [selectedProduct]);

  // ---------------------------------------------------------------------------------------------------

  const rotationText = imageRef.current && imageRef.current.offsetWidth > 577 ? "Rotate the sofa" : "Rotate"; 
  

  return (
    <div
      ref={containerRef}
      style={{ maxHeight: 'calc(85vh - 2rem)', maxWidth: '100%', position: 'relative' }}
      onClick={onImageClick} // Handle clicks on the image
>
      {/* <InstructionPopup /> */}
      {isEmptyRoom && <Popup 
        vendorColor={vendorColor}
        text={`Make your space just right:

* Rotate the sofa
* Move it by clicking on the floor`}
       />}
      <img
        ref={imageRef}
        src={imageSrc}
        alt="Selected Room"
        className="room-image"
        style={{
          maxHeight: 'calc(85vh - 2rem)',
          maxWidth: '100%',
          objectFit: 'contain',
        }} />

      {/* Rotation slider */}

      {(isStaged || isEmptyRoom) && 
      <div className={`absolute ${mobileScreen ? 'top-2 left-2 pl-2 pr-2' : 'top-4 left-4 p-2'} flex flex-row gap-4 z-20`}>
        <div
          onClick={onIgnoreClick}
          className={`${mobileScreen ? 'top-2 left-2 pl-2 pr-2' : 'top-4 left-4 p-2'} bg-white bg-opacity-80 shadow-lg rounded-lg`}
          style={{ 
            maxWidth: '400px', 
            backgroundColor: vendorColor,
            ...(mobileScreen ? {} : { top: 'calc(1rem)' }) }}
        >
          {/* Instruction to rotate */}
          <div
            className="flex items-center gap-4" // Flexbox layout with spacing
            onClick={onIgnoreClick}
          >
            <span className="text-white font-bold text-base">
              {mobileScreen ? <><RotateCcw size={24} /></> : <>{rotationText}</>}
            </span>
            <Slider
              aria-label="Rotation"
              value={rotationValueInSlider}
              onChange={handleRotationSliderChange} // Handles dragging updates
              onChangeCommitted={handleRotationSliderCommit} // Handles release or click 
              min={-90} // Minimum value
              max={90} // Maximum value
              marks={[{ value: 0, label: '' }]} // Highlight 0 with a label
              sx={{ color: 'white', width: mobileScreen ? '150px' : '200px' }} // Adjust the slider width as needed
            />
            {/* 'click on floor' instruction */}
          </div>
        </div>
        {/* Buttons container */}
        {!isEmptyRoom && isInteractingNow && <div className="flex gap-2"> {/* Adjust gap as needed */}
          <button
            style={{ 
              backgroundColor: vendorColor,
            }}
            className={`${buttonBaseClasses} z-20 text-white font-bold`}
            onClick={onGenerateClick}
            title="Generate"
            disabled={!isInteractingNow}
          >
            
            {!mobileScreen && <Rocket className="w-5 h-5" />}
            <span>Generate</span>
          </button>
        </div>}
      </div>}

      {/* Sofa Element */}
      {isInteractingNow && (
        <img
          src={sofaData.imageSrc}
          alt=""
          className="absolute"
          style={{
            left: `0px`,
            top: `0px`,
            backgroundColor: 'transparent',  // Ensure transparency is supported
          }}
        />
      )}
    </div>
  );
};


const HighlightedImage = ({ 
    imageSrc, bbox, showBoundingBox, vendorColor, imageHash, isEmptyRoom, selectedProduct, analysisResult, 
    setInteractiveSofaLoading, mobileScreen, isStaged, stageInteractiveSofa, stagingResult, setStagingResult, setProductStagingLoading,
    InteractiveSofaArgs}) => {
  const [scaledBbox, setScaledBbox] = useState(null);
  const imageRef = useRef(null); // Reference to the image element

  const calculateScaledBbox = () => {
    const img = imageRef.current;
    setScaledBbox(null); 
    if (!img) return;
    if (bbox.length == 0) return;
    if (!showBoundingBox) return; 

    const { naturalWidth, naturalHeight } = img;
    const renderedWidth = img.offsetWidth;
    const renderedHeight = img.offsetHeight;

    // Calculate scaling factors
    const scaleX = renderedWidth / naturalWidth;
    const scaleY = renderedHeight / naturalHeight;

    // Scale the bounding box
    const [x1, y1, x2, y2] = bbox.map((coord, index) =>
      index % 2 === 0 ? coord * scaleX : coord * scaleY
    );
    setScaledBbox([x1, y1, x2, y2]);
  };

  useEffect(() => {
    // Initial calculation when the image loads
    calculateScaledBbox();

    // Recalculate on window resize
    const handleResize = () => calculateScaledBbox();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [bbox]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <InteractiveSofaComponent 
          imageSrc={imageSrc} 
          imageRef={imageRef} 
          vendorColor={vendorColor} 
          imageHash={imageHash} 
          selectedProduct={selectedProduct}
          analysisResult={analysisResult}
          setInteractiveSofaLoading={setInteractiveSofaLoading}
          mobileScreen={mobileScreen}
          isEmptyRoom={isEmptyRoom}
          isStaged={isStaged}
          stageInteractiveSofa={stageInteractiveSofa}
          stagingResult={stagingResult}
          setStagingResult={setStagingResult}
          setProductStagingLoading={setProductStagingLoading}
          InteractiveSofaArgs={InteractiveSofaArgs}
        />

      {!isEmptyRoom && scaledBbox && (
        <>
          
          <div
            style={{
              position: "absolute",
              top: `${scaledBbox[1]}px`,
              left: `${scaledBbox[0]}px`,
              width: `${scaledBbox[2] - scaledBbox[0]}px`,
              height: `${scaledBbox[3] - scaledBbox[1]}px`,
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              pointerEvents: "none", // Ensure the box doesn't interfere with image interaction
            }}
          ></div>
        </>
      )}
    </div>
  );
};


function MainImageContainer(stagingResult, setStagingResult, analysisResult, vendorColor, hideStagedSofa, showBoundingBox,
                            mobileScreen, selectedProduct, setInteractiveSofaLoading, stageInteractiveSofa, setProductStagingLoading,
                            InteractiveSofaArgs) {
  return (
    <div className="w-full md:w-[75%] lg:w-[60%] mt-2 flex items-start justify-center relative">
      {/* Set the parent container to relative */}
      <div
        className="max-h-full max-w-full flex justify-center items-center relative"
        style={{ overflow: 'hidden' }}
      >
        {/* Conditionally render images based on staging or analysis results */}
        {!hideStagedSofa && stagingResult?.room_image_staged ? (
          <HighlightedImage
          imageSrc={`data:image/png;base64,${stagingResult?.room_image_staged}`}
          bbox={[]} // Pass the bounding box coordinates here
          showBoundingBox={false}
          vendorColor={vendorColor}
          imageHash={analysisResult.image_hash}
          isEmptyRoom={analysisResult.is_empty_room}
          selectedProduct={selectedProduct}
          analysisResult={analysisResult}
          setInteractiveSofaLoading={setInteractiveSofaLoading}
          mobileScreen={mobileScreen}
          isStaged={true}
          stageInteractiveSofa={stageInteractiveSofa}
          stagingResult={stagingResult}
          setStagingResult={setStagingResult}
          setProductStagingLoading={setProductStagingLoading}
          InteractiveSofaArgs={InteractiveSofaArgs}
          />
        ) : (
            analysisResult && (
              <HighlightedImage
              imageSrc={`data:image/png;base64,${analysisResult.room_image_original}`}
              bbox={analysisResult.bbox} // Pass the bounding box coordinates here
              showBoundingBox={showBoundingBox}
              vendorColor={vendorColor}
              imageHash={analysisResult.image_hash}
              isEmptyRoom={analysisResult.is_empty_room}
              selectedProduct={selectedProduct}
              analysisResult={analysisResult}
              setInteractiveSofaLoading={setInteractiveSofaLoading}
              mobileScreen={mobileScreen}
              isStaged={false}
              stageInteractiveSofa={stageInteractiveSofa}
              stagingResult={stagingResult}
              setStagingResult={setStagingResult}
              setProductStagingLoading={setProductStagingLoading}
              InteractiveSofaArgs={InteractiveSofaArgs}
              />)
        )}
      </div>
    </div>
  );
}

function MobileHeader({vendorColor, handleCheckout, handleSaveEmail, email, setEmail, GoToRoomSelection, hideStagedSofa, setHideStagedSofa,
                        setShowBoundingBox, hasStagedImage, ratingArgs, analysisResult}) {
  return <div className="header bg-white py-1 flex items-center gap-2 justify-center flex-wrap">
    <div className="w-full max-w-[1000px]">
      {/* Product Page Button */}
      <button
        style={{ backgroundColor: vendorColor }}
        className={`${buttonBaseClasses} text-white font-bold`}
        onClick={handleCheckout}
        title="Go to Product Page"
      >
        <ShoppingCart className="w-5 h-5" />
        <span>Product Page</span>
      </button>

      {/* Email Form */}
      <div className="mt-2">
          <EmailForm
            handleSaveEmail={handleSaveEmail}
            email={email}
            setEmail={setEmail}
            vendorColor={vendorColor}
            />
        </div>
    </div>

    <EngagementBar 
          GoToRoomSelection={GoToRoomSelection} 
          vendorColor={vendorColor} 
          hideStagedSofa={hideStagedSofa} 
          setHideStagedSofa={setHideStagedSofa} 
          setShowBoundingBox={setShowBoundingBox} 
          hasStagedImage={hasStagedImage}
          ratingArgs={ratingArgs} 
          isMobile={true}
          usingInteractiveSofaComponent={analysisResult?.is_empty_room ?? false}
        />
  </div>;
}

function FilterOptionsComponent(product_size_options, selectedProductSizes, handleProductSizeChange, product_color_options, productColorHtmlMap, 
                                selectedProductColors, handleProductColorChange, handleClearFilters, vendorColor, handleDone) {
  return <div className="filters bg-white p-4 rounded-xl mt-2">
    {/* Product Size Filter Section */}
    <div className="size-filter mb-6">
      <p className="font-bold text-lg mb-4">Size</p>
      {product_size_options.map((size, index) => (
        <label
          key={index}
          className="flex items-center w-full hover:bg-gray-100 p-2" // Ensure label is full width
        >
          <span className="text-lg flex-grow">{size}</span> {/* Text takes up remaining space */}
          <input
            type="checkbox"
            name={size}
            className="w-5 h-5 ml-2" // Adjust margin for checkbox
            checked={selectedProductSizes.includes(size)} // Track the checked state
            onChange={() => handleProductSizeChange(size)} // Handle change
          />
        </label>
      ))}
    </div>

    {/* Product Color Filter Section */}
    <div className="color-filter mb-6">
      <p className="font-bold text-lg mb-4">Color</p>
      {product_color_options.map((color, index) => {
        // Check if the color is in the productColorHtmlMap, otherwise default to white
        const colorFill = productColorHtmlMap[color] || "#FFFFFF"; // Default to white if color is missing
        let borderColor = "#000000"; // Default to black outline
        if (productColorHtmlMap[color] === undefined) {
          borderColor = "#FFFFFF"; // White outline for missing colors
        }

        return (
          <label
            key={index}
            className="flex items-center w-full hover:bg-gray-100 p-2" // Full-width label with padding
          >
            {/* Color box with dynamic background and border color */}
            <div
              className="color-box w-8 h-8 rounded-md"
              style={{
                backgroundColor: colorFill, // Fill the rectangle with the color
                border: `2px solid ${borderColor}`, // Use borderColor
              }} />
            {/* Text aligned to the left, taking up remaining space */}
            <span className="text-lg flex-grow ml-2">{color}</span>
            {/* Checkbox aligned to the right */}
            <input
              type="checkbox"
              name={color}
              className="w-5 h-5 ml-auto"
              checked={selectedProductColors.includes(color)} // Track the checked state
              onChange={() => handleProductColorChange(color)} // Handle change
            />
          </label>
        );
      })}
    </div>

    {/* Clear and Done Buttons */}
    <div className="flex justify-between items-center mt-6">
      <button
        className="clear-filters bg-gray-200 text-[#5A6066] px-6 py-3 rounded-lg hover:bg-gray-300 text-lg w-full max-w-[200px] mr-4"
        onClick={handleClearFilters}
      >
        Clear Filters
      </button>
      <button
        style={{ backgroundColor: vendorColor }}
        className="done text-white px-6 py-3 rounded-lg hover:bg-blue-600 text-lg w-full max-w-[200px] ml-4"
        onClick={handleDone}
      >
        Done
      </button>
    </div>
  </div>;
}

function FilterButton(vendorColor, handleFilterClick, getAppliedFiltersCount, showFilters) {
  return <div
    style={showFilters === true ? { borderColor: vendorColor } : { borderColor: "gray" }}
    className="w-1/2 rounded-xl border-2 text-center cursor-pointer hover:shadow-lg"
  >
    <div className="w-full flex justify-center">
      <button
        className="flex items-center justify-center gap-1 py-2 px-4 w-full font-semibold text-[11px] text-[#000000] cursor-pointer hover:underline"
        onClick={handleFilterClick}
      >
        <img src="/assets/icons/filter.svg" alt="filter-icon" className="w-4 h-4" />
        {getAppliedFiltersCount() > 0 ? `${getAppliedFiltersCount()}` : "Filters"}
      </button>
    </div>
  </div>;
}

function TabContainer(tabs, activeTab, vendorColor, handleTabClick) {
  return <div className="flex border border-[#E3E9EE] rounded-xl p-1 mt-2 gap-4">
    {tabs.map((tab, index) => (
      <div
        key={index}
        className={`w-[50%] rounded-xl text-center text-${activeTab === tab ? "white" : "[#44484C]"}`}
        style={activeTab === tab ? { backgroundColor: vendorColor } : { backgroundColor: "gray" }}
      >
        <button className="py-2 font-semibold" onClick={() => handleTabClick(tab)}>
          {tab}
        </button>
      </div>
    ))}
  </div>;
}


export default RoomAnalysisStagingStep;
